import { isEventAddOn } from '@/utils/cart.utils'

/**
 * Checks if `lineItem` is an Add-On. Returns `[]` if it is, otherwise looks in the cart for
 * add-ons for this event
 * @param lineItem {Transformed.LineItem} - LineItem of interest
 * @param cartData {Cart} - The current cart
 */
const getAddOnsForEvent = (lineItem: Transformed.LineItem, cartData: Transformed.Cart): Transformed.LineItem[] => (
  lineItem.category.meta?.find(isEventAddOn)
    ? []
    // if the line item is an Event but not an add-on, then find related add-ons in cart
    : cartData.lineItems
      .filter((item) => (
        item.event.id === lineItem.event.id
        && item.category.meta?.find(isEventAddOn))))

/**
 * Gets items from cart that have the provided `eventId` and are not add-ons
 * @param cartData {Cart} - the current cart
 * @param eventId {string} - the event id to search the cart for
 */
const getEventLineItems = (cartData: Transformed.Cart, eventId: string): Transformed.LineItem[] => (
  cartData.lineItems
    .filter((item) => (
      item.event.id === eventId
      && !item.category.meta?.find(isEventAddOn)))
)

/**
 * Counts the total number of line items in the provided array of {@link LineItem}s
 * @param eventLineItems {LineItem[]} - an array of line items to determine the total quantity of
 */
const getNumEventTix = (eventLineItems: Transformed.LineItem[]): number => (
  eventLineItems
    .reduce((totalTix: number, item) => totalTix + item.quantity, 0)
)

/**
 * Updates the `currentLineItems` array with the `lineItemsToAdd`. `numberOfLineItemsToRemove` indicates
 * how the quantity of `lineItemsToAdd` should be deprecated by
 * @param currentLineItems {Transformed.LineItem[]} - The current array of line items to be appended
 * @param lineItemsToAdd {Transformed.LineItem[]} - An array of line items to add to `currentLineItems`
 * @param numberOfLineItemsToRemove {number} - The number to remove from the quantity of items in `lineItemsToAdd`
 */
const updateLineItemsWithDecrement = (
  currentLineItems: Transformed.LineItem[],
  lineItemsToAdd: Transformed.LineItem[],
  numberOfLineItemsToRemove: number,
): Transformed.LineItem[] => (
  currentLineItems.concat(
    lineItemsToAdd.map((addOn) => (
      {
        ...addOn,
        // the number of add-ons to remove
        quantity: addOn.quantity - numberOfLineItemsToRemove,
      }
    )),
  ))

/**
 * Updates the `tickets` array with the `ticketsToAdd`. `numberOfTicketsToRemove` indicates
 * how the quantity of `ticketsToAdd` should be deprecated by
 * @param tickets {string[]} - an array of ticketIds
 * @param ticketsToAdd {string[]} - and array of ticketIds to append to `tickets`
 * @param numberOfTicketsToRemove {number} - the number of tickets to remove from `ticketsToAdd`
 */
const updateTicketsWithDecrement = (
  tickets: string[],
  ticketsToAdd: Transformed.LineItem[],
  numberOfTicketsToRemove: number,
): string[] => (
  tickets.concat(
    ticketsToAdd
      .map((addOn) => addOn.ticketIds.slice(0, addOn.quantity - numberOfTicketsToRemove) as string[])
      .flat(),
  ))

/**
 * Handles the logic for deleting tickets from the cart. If not an event (or an event with no add-ons), simply just
 * calls `handleDelete` on the `lineItem` and `ticketIds`. If it is an event and there are add-ons, determines if the
 * quantity of each add-on exceeds the new number of event tickets. If so, updates `lineItem` and `ticketIds` to include
 * add-ons such that their quantity is equal to the number of remaining event tickets
 * @param lineItem {LocalLineItem} - the line item being deleted
 * @param ticketIds {string[]} - the ticketIds to delete, corresponding to the `lineItem`
 * @param isEventLineItem {boolean} - is this for an event (non-exhibition)?
 * @param handleDelete {(DeleteArgs) => void} - the function used to delete lineItems and ticketIds
 * @param cartData {Cart} - the current cart
 */
export const getDeleteClickHandler = (
  lineItem: Transformed.LineItem,
  ticketIds: string[],
  isEventLineItem: boolean,
  handleDelete: ((args: DeleteArgs) => void) | undefined,
  cartData: Transformed.Cart,
) => (): void => {
  let lineItems = [lineItem]
  let tickets = [...ticketIds]

  if (isEventLineItem && handleDelete) {
    const addOns = getAddOnsForEvent(lineItem, cartData)
    // if addOns for this event exist
    if (addOns.length > 0) {
      // get all eventLineItems corresponding to this event id (and are not add-ons)
      const eventLineItems = getEventLineItems(cartData, lineItem.event.id)

      if (eventLineItems.length > 0) {
        // get number of tickets before deletion
        const numEventTix = getNumEventTix(eventLineItems)

        // get number of tickets after deletion
        const newNumEventTix = numEventTix - lineItem.quantity

        // get add-ons with a quantity that exceed the new number of tix
        const addOnsToRemove = addOns
          .filter((addOn) => addOn.quantity > newNumEventTix)

        // add these as line items to be removed
        lineItems = updateLineItemsWithDecrement(lineItems, addOnsToRemove, newNumEventTix)

        // add a number of ticketIds corresponding to add-ons to be removed
        tickets = updateTicketsWithDecrement(tickets, addOnsToRemove, newNumEventTix)
      }
    }
  }

  if (handleDelete) {
    handleDelete({
      tickets,
      lineItems,
    })
  }
}

export const exportedForTesting = {
  getAddOnsForEvent,
  getEventLineItems,
  getNumEventTix,
  updateLineItemsWithDecrement,
  updateTicketsWithDecrement,
}
